import React, { useState, useEffect } from 'react';
import {
  Box,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  Dialog,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import BootstrapDialog, {
  BootstrapDialogTitle,
} from '../../common/dialog/BootstrapDialog';
import * as projectApi from '../../../lib/api/project';
import { isNativeApp, openPdfNatively } from '../../../lib/nativeDownload';

const OrderSheetPreviewModal = ({
  type,
  open,
  handleClose,
  orderSheet,
}) => {
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open && orderSheet?.prjUuid && orderSheet?.spoUuid) {
      loadPdfPreview();
    }
    return () => {
      if (pdfPreviewUrl) {
        window.URL.revokeObjectURL(pdfPreviewUrl);
        setPdfPreviewUrl(null);
      }
    };
  }, [open, orderSheet?.spoUuid]);

  const loadPdfPreview = async () => {
    setLoading(true);
    try {
      const blob = await projectApi.downloadOrderSheetPdf(
        orderSheet.prjUuid,
        orderSheet.spoUuid,
      );
      const pdfBlob = new Blob([blob.data], { type: 'application/pdf' });
      // 네이티브(Android) 앱: WebView가 iframe으로 PDF를 못 그리므로 미리보기 Dialog를
      // 띄우는 대신 폰의 PDF 뷰어로 바로 열고 이 Dialog는 닫는다.
      if (isNativeApp()) {
        await openPdfNatively(pdfBlob, `발주서_${orderSheet?.sheetCstNm || 'order'}.pdf`);
        handleClose();
        return;
      }
      const pdfUrl = window.URL.createObjectURL(pdfBlob);
      setPdfPreviewUrl(pdfUrl);
    } catch (err) {
      console.error('PDF 미리보기 로드 실패:', err);
      if (isNativeApp()) {
        enqueueSnackbar('PDF 미리보기 실패: ' + (err?.message || err), { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePdfDownload = () => {
    if (pdfPreviewUrl) {
      const link = document.createElement('a');
      link.href = pdfPreviewUrl;
      link.download = (orderSheet?.sheetCstNm || '발주서') + '.pdf';
      link.click();
    }
  };

  const onClose = () => {
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
    handleClose();
  };

  return (
    <BootstrapDialog
      open={open}
      onClose={onClose}
      aria-labelledby="draggable-preview-dialog"
      fullWidth={true}
      maxWidth="lg"
    >
      <BootstrapDialogTitle
        style={{ cursor: 'move' }}
        id="draggable-preview-dialog"
        onClose={onClose}
      >
        발주서 미리보기
      </BootstrapDialogTitle>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DialogContent
          dividers
          sx={{
            height: '70vh',
            p: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {loading ? (
            <CircularProgress />
          ) : pdfPreviewUrl ? (
            <iframe
              src={pdfPreviewUrl}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title="발주서 PDF 미리보기"
            />
          ) : (
            <Box>PDF를 불러올 수 없습니다.</Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="warning" onClick={handlePdfDownload} disabled={!pdfPreviewUrl}>
            PDF 다운로드
          </Button>
        </DialogActions>
      </Box>
    </BootstrapDialog>
  );
};

export default OrderSheetPreviewModal;
